import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';

const APKTestPage: React.FC = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<any[]>([]);
  const [apkVersions, setApkVersions] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [devicesResponse, apkVersionsResponse] = await Promise.all([
        fetch(API_ENDPOINTS.DEVICES),
        fetch(API_ENDPOINTS.APK_VERSIONS)
      ]);

      if (!devicesResponse.ok) {
        throw new Error(`设备API错误: ${devicesResponse.status}`);
      }

      if (!apkVersionsResponse.ok) {
        throw new Error(`APK版本API错误: ${apkVersionsResponse.status}`);
      }

      const devicesData = await devicesResponse.json();
      const apkVersionsData = await apkVersionsResponse.json();

      setDevices(devicesData);
      setApkVersions(apkVersionsData);
    } catch (err: any) {
      setError(err.message);
      console.error('加载数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('apkTestPage.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={loadData} variant="contained">
          {t('apkTestPage.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('apkTestPage.title')}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('apkTestPage.deviceList')} ({devices.length} {t('apkTestPage.devices')})
        </Typography>
        <List>
          {devices.map((device) => (
            <React.Fragment key={device.id}>
              <ListItem>
                <ListItemText
                  primary={`${device.name} (${device.ip})`}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('apkTestPage.status')}: {device.status} | {t('apkTestPage.location')}: {device.location}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('apkTestPage.apkCount')}: {device.apks?.length || 0}
                      </Typography>
                      {device.apks && device.apks.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          {device.apks.map((apk: any) => (
                            <Typography key={apk.id} variant="caption" display="block">
                              • {apk.name} ({apk.paymentSystem}) - {apk.currentEnvironment}
                              {apk.isActive && ` [${t('apkTestPage.activated')}]`}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('apkTestPage.apkVersionList')} ({Object.keys(apkVersions).length} {t('apkTestPage.apks')})
        </Typography>
        <List>
          {Object.entries(apkVersions).map(([apkId, apkInfo]: [string, any]) => (
            <React.Fragment key={apkId}>
              <ListItem>
                <ListItemText
                  primary={`${apkInfo.name} (${apkId})`}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('apkTestPage.packageName')}: {apkInfo.packageName} | {t('apkTestPage.paymentSystem')}: {apkInfo.paymentSystem}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('apkTestPage.versionCount')}: {Object.keys(apkInfo.versions).length}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </Box>

      <Button onClick={loadData} variant="contained">
        刷新数据
      </Button>
    </Box>
  );
};

export default APKTestPage;
